import React, { useState } from 'react';
import { APIKey } from '../../types/apiKeys';
import { apiKeyService } from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';

interface TestAPIKeyModalProps {
  apiKey: APIKey;
  onClose: () => void;
}

export const TestAPIKeyModal: React.FC<TestAPIKeyModalProps> = ({ apiKey, onClose }) => {
  const { showToast } = useToast();
  const [testEndpoint, setTestEndpoint] = useState('/api/v1/user/profile/');
  const [testMethod, setTestMethod] = useState('GET');
  const [testData, setTestData] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);

  const availableEndpoints = [
    { value: '/api/v1/user/profile/', label: 'User Profile (GET)', method: 'GET' },
    { value: '/api/v1/credits/balance/', label: 'Credits Balance (GET)', method: 'GET' },
    { value: '/api/v1/health/', label: 'Health Check (GET)', method: 'GET' },
    { value: '/api/v1/test-auth/', label: 'Test Auth (GET)', method: 'GET' },
    { value: '/api/v1/test-token/', label: 'Test Token (GET)', method: 'GET' },
    { value: '/api/v1/osint/', label: 'OSINT APIs (GET)', method: 'GET' },
    { value: '/api/v1/account-finder/find-accounts/', label: 'Account Finder (POST)', method: 'POST' },
    { value: '/api/v1/search/', label: 'Create Search (POST)', method: 'POST' },
  ];

  const handleTest = async () => {
    setIsLoading(true);
    setTestResult(null);

    try {
      // Parse test data if provided
      let parsedTestData = {};
      if (testMethod === 'POST' && testData.trim()) {
        try {
          parsedTestData = JSON.parse(testData);
        } catch (e) {
          showToast('Invalid JSON in test data', 'error');
          setIsLoading(false);
          return;
        }
      }

      const testPayload = {
        api_key_id: apiKey.id,
        endpoint: testEndpoint,
        method: testMethod,
        test_data: parsedTestData
      };

      console.log('Test Payload:', testPayload);
      console.log('API Key display_key:', apiKey.display_key);
      console.log('API Key object:', apiKey);

      const result = await apiKeyService.testAPIKeyAccess(testPayload);
      setTestResult(result);
      showToast('API key test completed successfully', 'success');
    } catch (error: any) {
      console.error('Test Error:', error);
      setTestResult({
        success: false,
        error: error.message || 'Test failed',
        status_code: error.status || 500,
        api_key_name: apiKey.name
      });
      showToast('API key test failed', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Test API Key: {apiKey.name}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            ✕
          </button>
        </div>

        <div className="space-y-4">
          {/* API Key Info */}
          <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
            <h3 className="font-semibold mb-2">API Key Details</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="font-medium">Key:</span> {apiKey.display_key}</div>
              <div><span className="font-medium">Status:</span> {apiKey.status}</div>
              <div><span className="font-medium">Type:</span> {apiKey.key_type}</div>
              <div><span className="font-medium">Rate Limit:</span> {apiKey.rate_limit}/hour</div>
            </div>
          </div>

          {/* Test Configuration */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Endpoint
              </label>
                             <select
                 value={testEndpoint}
                 onChange={(e) => {
                   const selectedEndpoint = availableEndpoints.find(ep => ep.value === e.target.value);
                   setTestEndpoint(e.target.value);
                   if (selectedEndpoint) {
                     setTestMethod(selectedEndpoint.method);
                   }
                 }}
                 className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md"
               >
                 {availableEndpoints.map((endpoint, index) => (
                   <option key={`${endpoint.value}-${endpoint.method}-${index}`} value={endpoint.value}>
                     {endpoint.label}
                   </option>
                 ))}
               </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Method
              </label>
              <select
                value={testMethod}
                onChange={(e) => setTestMethod(e.target.value)}
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md"
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>

                         {testMethod === 'POST' && (
               <div>
                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                   Request Data (JSON)
                 </label>
                 <textarea
                   value={testData}
                   onChange={(e) => setTestData(e.target.value)}
                   placeholder={
                     testEndpoint === '/api/v1/account-finder/find-accounts/' 
                       ? '{"parameter_type": 1, "search_value": "test@example.com"}'
                       : testEndpoint === '/api/v1/search/'
                       ? '{"parameter_type": 1, "search_value": "test@example.com", "description": "Test search"}'
                       : '{"key": "value"}'
                   }
                   className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md h-24"
                 />
                 <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                   {testEndpoint === '/api/v1/account-finder/find-accounts/' && 
                     'Sample: {"parameter_type": 1, "search_value": "test@example.com"}'}
                   {testEndpoint === '/api/v1/search/' && 
                     'Sample: {"parameter_type": 1, "search_value": "test@example.com", "description": "Test search"}'}
                 </div>
               </div>
             )}
          </div>

          {/* Test Button */}
          <button
            onClick={handleTest}
            disabled={isLoading}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {isLoading ? 'Testing...' : 'Test API Key'}
          </button>

                     {/* Test Result */}
           {testResult && (
             <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
               <h3 className="font-semibold mb-2">Test Result</h3>
               <div className="space-y-4">
                 {/* Status and Basic Info */}
                 <div className="grid grid-cols-2 gap-4 text-sm">
                   <div className="flex items-center gap-2">
                     <span className={`w-3 h-3 rounded-full ${testResult.success ? 'bg-green-500' : 'bg-red-500'}`}></span>
                     <span className="font-medium">
                       {testResult.success ? 'Success' : 'Failed'}
                     </span>
                   </div>
                   {testResult.status_code && (
                     <div><span className="font-medium">Status:</span> {testResult.status_code}</div>
                   )}
                   {testResult.response_time && (
                     <div><span className="font-medium">Response Time:</span> {testResult.response_time}ms</div>
                   )}
                   {testResult.remaining_requests !== undefined && (
                     <div><span className="font-medium">Remaining Requests:</span> {testResult.remaining_requests}</div>
                   )}
                 </div>

                 {/* Request Details */}
                 {testResult.request_url && (
                   <div className="bg-white dark:bg-gray-800 p-3 rounded border">
                     <h4 className="font-medium text-sm mb-2">Request Details</h4>
                     <div className="space-y-1 text-xs">
                       <div><span className="font-medium">URL:</span> {testResult.request_url}</div>
                       <div><span className="font-medium">Method:</span> {testResult.request_method}</div>
                       {testResult.request_headers && (
                         <div>
                           <span className="font-medium">Headers:</span>
                           <pre className="mt-1 bg-gray-100 dark:bg-gray-800 p-2 rounded text-xs overflow-x-auto">
                             {JSON.stringify(testResult.request_headers, null, 2)}
                           </pre>
                         </div>
                       )}
                     </div>
                   </div>
                 )}

                 {/* Response Data */}
                 {testResult.data && (
                   <div className="bg-white dark:bg-gray-800 p-3 rounded border">
                     <h4 className="font-medium text-sm mb-2">Response Data</h4>
                     <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-3 rounded border overflow-x-auto max-h-64">
                       {JSON.stringify(testResult.data, null, 2)}
                     </pre>
                   </div>
                 )}

                 {/* Error Information */}
                 {testResult.error && (
                   <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded border">
                     <h4 className="font-medium text-sm mb-2 text-red-700 dark:text-red-300">Error Response</h4>
                     <pre className="text-xs bg-red-100 dark:bg-red-900/30 p-3 rounded border overflow-x-auto max-h-64 text-red-800">
                       {typeof testResult.error === 'string' ? testResult.error : JSON.stringify(testResult.error, null, 2)}
                     </pre>
                   </div>
                 )}
               </div>
             </div>
           )}
        </div>
      </div>
    </div>
  );
};
